import React from 'react';
import { motion } from 'framer-motion';

const PoweredByTechnology: React.FC = () => {
  const features = [
    "Submit unlimited requests and track progress in real-time",
    "Manage multiple brands and projects from one dashboard",
    "Direct communication with your dedicated design team",
    "Collaborate with your team and share feedback instantly"
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <p className="text-primary text-sm font-semibold uppercase tracking-wide mb-4">
              POWERED BY TECHNOLOGY
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
              Everything you need to scale your design operations
            </h2>
            <div className="space-y-4">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center space-x-4"
                >
                  <div className="w-3 h-3 bg-primary rounded-sm flex-shrink-0"></div>
                  <p className="text-lg text-gray-700">{feature}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Column - UI Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Abstract geometric shapes */}
            <div className="absolute -top-8 -left-8 w-6 h-6 bg-yellow-400 rounded-sm"></div>
            <div className="absolute -top-4 -left-4 w-4 h-4 bg-primary rounded-sm"></div>
            <div className="absolute -bottom-6 -left-6 w-5 h-5 bg-primary rounded-sm"></div>
            
            {/* Connecting lines */}
            <div className="absolute -top-6 -left-6 w-0.5 h-8 bg-gray-300"></div>
            <div className="absolute -bottom-3 -left-3 w-0.5 h-6 bg-gray-300"></div>

            {/* UI Mockup */}
            <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
              {/* Header */}
              <div className="bg-secondary px-6 py-4">
                <h3 className="text-white font-semibold">Project Dashboard</h3>
              </div>
              
              {/* File bar */}
              <div className="bg-gray-100 px-6 py-3 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-700">Brand Assets</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </div>
              </div>

              {/* Main content area */}
              <div className="flex">
                {/* Main image area */}
                <div className="flex-1 p-6 relative">
                  <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4 h-64 relative">
                    {/* Summer Campaign Mockup */}
                    <div className="bg-white rounded-lg p-3 shadow-sm mb-3">
                      <div className="text-center">
                        <h4 className="font-bold text-gray-800 text-sm">SUMMER CAMPAIGN</h4>
                        <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full mx-auto my-2 flex items-center justify-center">
                          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    
                    {/* Mobile mockup */}
                    <div className="bg-gray-800 rounded-lg p-2 w-20 h-32 mx-auto mb-3">
                      <div className="bg-white rounded w-full h-full"></div>
                    </div>
                    
                    {/* 10% OFF card */}
                    <div className="bg-primary text-white rounded-lg p-2 text-center text-xs font-bold w-16 mx-auto">
                      10% OFF
                    </div>

                    {/* Feedback pins */}
                    <div className="absolute top-4 right-4 w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white text-xs font-bold">
                      1
                    </div>
                    <div className="absolute bottom-8 right-4 w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white text-xs font-bold">
                      2
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="w-64 bg-gray-50 p-4 border-l border-gray-200">
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 text-sm">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                      </svg>
                      <span className="text-gray-700">Logo Design Final.psd</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                      </svg>
                      <span className="text-gray-700">Social Media Kit.png</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-2">
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                        </svg>
                        <span className="text-gray-700">Website Mockup v2.jpg</span>
                      </div>
                      <span className="w-5 h-5 bg-primary rounded-full flex items-center justify-center text-white text-xs font-bold">3</span>
                    </div>
                    <div className="pt-2">
                      <button className="text-primary text-sm font-medium hover:text-primary-dark transition-colors">
                        Download All
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PoweredByTechnology; 